import { useState, useEffect } from 'react';

/**
 * useIsMobile Hook
 * Détecte si l'utilisateur est sur un appareil mobile
 *
 * Utilise window.matchMedia pour détecter les écrans <= 768px
 * S'actualise automatiquement quand la taille de l'écran change
 *
 * @returns boolean - true si mobile, false sinon
 *
 * @example
 * const isMobile = useIsMobile();
 * if (isMobile) {
 *   // Afficher la version mobile
 * }
 */
export function useIsMobile(breakpoint: number = 768): boolean {
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth <= breakpoint;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia(`(max-width: ${breakpoint}px)`);

    const handleChange = (e: MediaQueryListEvent | MediaQueryList) => {
      setIsMobile(e.matches);
    };

    // Initial check
    handleChange(mediaQuery);

    // Listen for changes
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    } else {
      // Fallback for older browsers
      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }
  }, [breakpoint]);

  return isMobile;
}
